import { Component, ElementRef, EventEmitter, HostListener, Input, Output, SimpleChanges } from '@angular/core';
import { AlertService } from '../../../services/shared/alert.service';

export interface DateTimeRangeResult {
  startDate: string; // "YYYY-MM-DD"
  startTime: string; // "HH:00:ss" (24hr standard format for APIs)
  endDate: string;   // "YYYY-MM-DD"
  endTime: string;   // "HH:00:ss"
}

@Component({
  selector: 'app-time-range-picker',
  templateUrl: './time-range-picker.component.html',
  styleUrl: './time-range-picker.component.css'
})
export class TimeRangePickerComponent {
  @Input() label: string = '';
  @Input() startTime: string = ''; 
  @Input() endTime: string = '';
  @Output() dateTimeRangeChange = new EventEmitter<DateTimeRangeResult>(); 

  isDropdownOpen = false;
  startDate: string = ''; // Internal ISO storage: "YYYY-MM-DD"
  endDate: string = '';   // Internal ISO storage: "YYYY-MM-DD"

  startHr = '09';
  startMin = '00'; 
  startAmPm = 'AM';

  endHr = '05';
  endMin = '00';  
  endAmPm = 'PM';

  hours: string[] = [];
  minutes: string[] = []; 
  warningMessage = '';

  constructor(private elementRef: ElementRef, private alertService: AlertService) {}

  ngOnInit() {
    for (let i = 1; i <= 12; i++) {
      this.hours.push(i < 10 ? '0' + i : '' + i);
    }
    for (let i = 0; i < 60; i++) {
      this.minutes.push(i < 10 ? '0' + i : '' + i);
    }
    this.parseIncomingProps();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['startTime'] || changes['endTime']) {
      const startChange = changes['startTime'];

      if (startChange?.currentValue && startChange.currentValue !== startChange.previousValue) {
        this.parseIncomingProps();
      }
    }
  }

  private parseIncomingProps() {
    if (!this.startTime || !this.endTime) {
      this.setToTodayAndEmit();
      return;
    }

    try {
      // 1. Parse Start Date and Time (Handles both MM/DD/YYYY and YYYY-MM-DD)
      const startParts = this.startTime.trim().split(/\s+/);
      this.startDate = this.normalizeToIsoDate(startParts[0]); 

      if (startParts[1]) {
        const [hh, mm] = startParts[1].split(':');
        const incomingAmPm = startParts[2] ? startParts[2].toUpperCase() : null;
        this.parseAndSetTime(parseInt(hh, 10), mm, 'start', incomingAmPm);
      }

      // 2. Parse End Date and Time
      const endParts = this.endTime.trim().split(/\s+/);
      this.endDate = this.normalizeToIsoDate(endParts[0]); 

      if (endParts[1]) {
        const [hh, mm] = endParts[1].split(':');
        const incomingAmPm = endParts[2] ? endParts[2].toUpperCase() : null;
        this.parseAndSetTime(parseInt(hh, 10), mm, 'end', incomingAmPm);
      }
    } catch (e) {
      console.error("Failed to parse incoming date/time strings:", e);
      this.setToTodayAndEmit();
    }
  }

  // Robust parsing utility to save the format as standard YYYY-MM-DD internally
  private normalizeToIsoDate(dateStr: string): string {
    if (!dateStr) return '';
    if (dateStr.includes('/')) {
      const [month, day, year] = dateStr.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateStr; // Fallback if already structured as YYYY-MM-DD
  }

  private parseAndSetTime(hour: number, minutes: string, target: 'start' | 'end', incomingAmPm: string | null) {
    let hour12 = hour;
    let ampm = 'AM';

    if (incomingAmPm === 'AM' || incomingAmPm === 'PM') {
      ampm = incomingAmPm;
      hour12 = hour;
    } else {
      if (hour >= 12) {
        ampm = 'PM';
        if (hour > 12) hour12 = hour - 12;
      }
      if (hour === 0) {
        hour12 = 12;
        ampm = 'AM';
      }
    }

    const paddedHour = String(hour12).padStart(2, '0');
    const paddedMin = String(minutes).padStart(2, '0');

    if (target === 'start') {
      this.startHr = paddedHour;
      this.startMin = paddedMin;
      this.startAmPm = ampm;
    } else {
      this.endHr = paddedHour;
      this.endMin = paddedMin;
      this.endAmPm = ampm;
    }
  }

  private setToTodayAndEmit() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    
    this.startDate = `${year}-${month}-${day}`;
    this.endDate = `${year}-${month}-${day}`;
    
    this.startHr = '12';
    this.startMin = '00';
    this.startAmPm = 'AM';
    this.endHr = '11';
    this.endMin = '59';
    this.endAmPm = 'PM';
  }

  toggleDropdown() {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  formatDateToUS(dateStr: string): string {
    if (!dateStr) return dateStr;
    if (dateStr.includes('/')) return dateStr; // Already US format
    const [year, month, day] = dateStr.split('-');
    return `${month}/${day}/${year}`;
  }

  getStartDisplayValue(): string {
    if (!this.startDate) return '(Pick Date)';
    if (!this.startHr) return this.formatDateToUS(this.startDate) + '<br/>' + '(Pick Time)';
    return `${this.formatDateToUS(this.startDate)} ${this.startHr}:${this.startMin} ${this.startAmPm}`;
  }

  getEndDisplayValue(): string {
    if (!this.endDate) return '(Pick Date)';
    if (!this.endHr) return this.formatDateToUS(this.endDate) + '<br/>' + '(Pick Time)';
    return `${this.formatDateToUS(this.endDate)} ${this.endHr}:${this.endMin} ${this.endAmPm}`;
  }

  // emitDateTimeRange() {
  //   if (!this.startDate || !this.endDate) return;

  //   const startParts = this.startDate.split('-');
  //   const endParts = this.endDate.split('-');
    
  //   const startObj = new Date(Number(startParts[0]), Number(startParts[1]) - 1, Number(startParts[2]));
  //   const endObj = new Date(Number(endParts[0]), Number(endParts[1]) - 1, Number(endParts[2]));
    
  //   const diffTime = endObj.getTime() - startObj.getTime();
  //   const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1;

  //   if (diffDays > 5) {
  //     this.warningMessage = 'The selected range exceeded 5 days, so the end date was adjusted automatically.';
      
  //     const maxEndDate = new Date(startObj);
  //     maxEndDate.setDate(startObj.getDate() + 4); 
      
  //     this.endDate = `${maxEndDate.getFullYear()}-${String(maxEndDate.getMonth() + 1).padStart(2, '0')}-${String(maxEndDate.getDate()).padStart(2, '0')}`;

  //     setTimeout(() => {
  //       this.warningMessage = '';
  //     }, 10000);
  //   } 
  //   else if (diffDays < 1) {
  //     this.endDate = this.startDate;
  //   }

  //   const finalStartHr = this.startHr || '12';     
  //   const finalStartMin = this.startMin || '00';
  //   const finalStartAmPm = this.startAmPm || 'AM'; 

  //   const finalEndHr = this.endHr || '12';         
  //   const finalEndMin = this.endMin || '00';
  //   const finalEndAmPm = this.endAmPm || 'AM';     

  //   const calculatedStartTime = `${this.convertTo24Hour(finalStartHr, finalStartAmPm)}:${finalStartMin}:00`;
  //   const calculatedEndTime = `${this.convertTo24Hour(finalEndHr, finalEndAmPm)}:${finalEndMin}:00`;

  //   this.dateTimeRangeChange.emit({
  //     startDate: this.startDate, 
  //     startTime: calculatedStartTime, 
  //     endDate: this.endDate,
  //     endTime: calculatedEndTime     
  //   });
  // }

  emitDateTimeRange() {
    if (!this.startDate || !this.endDate) return;

    const startParts = this.startDate.split('-');
    const endParts = this.endDate.split('-');
    
    const startObj = new Date(Number(startParts[0]), Number(startParts[1]) - 1, Number(startParts[2]));
    const endObj = new Date(Number(endParts[0]), Number(endParts[1]) - 1, Number(endParts[2]));
    
    const diffTime = endObj.getTime() - startObj.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1;

    if (diffDays > 5) {
      this.warningMessage = 'The selected range exceeded 5 days, so the end date was adjusted automatically.';
      
      const maxEndDate = new Date(startObj);
      maxEndDate.setDate(startObj.getDate() + 4); 
      
      this.endDate = `${maxEndDate.getFullYear()}-${String(maxEndDate.getMonth() + 1).padStart(2, '0')}-${String(maxEndDate.getDate()).padStart(2, '0')}`;

      setTimeout(() => {
        this.warningMessage = '';
      }, 10000);
    } 
    else if (diffDays < 1) {
      this.endDate = this.startDate;
    }

    const finalStartHr = this.startHr || '12';     
    const finalStartMin = this.startMin || '00';
    const finalStartAmPm = this.startAmPm || 'AM'; 

    const finalEndHr = this.endHr || '12';         
    const finalEndMin = this.endMin || '00';
    const finalEndAmPm = this.endAmPm || 'AM';     

    // 1. Re-calculate the times in standard 12 Hour formatting
    const formattedStartTimeStr = `${this.startHr}:${this.startMin}:00 ${this.startAmPm}`;
    const formattedEndTimeStr = `${this.endHr}:${this.endMin}:00 ${this.endAmPm}`;

    // 2. Format the base dates into clean MM/DD/YYYY representations
    const usStartDate = this.formatDateToUS(this.startDate); // Converts "2026-06-19" -> "06/19/2026"
    const usEndDate = this.formatDateToUS(this.endDate);     // Converts "2026-06-19" -> "06/19/2026"

    // 3. Emit matching custom strings back up to update parentStartTime & parentEndTime 
    this.dateTimeRangeChange.emit({
      startDate: usStartDate, 
      startTime: formattedStartTimeStr, // Yields: "06/19/2026 12:00:00 AM" cleanly to your layout labels
      endDate: usEndDate,
      endTime: formattedEndTimeStr     
    });
  }

  private convertTo24Hour(hour12Str: string, ampm: string): string {
    let hour = parseInt(hour12Str, 10);
    if (ampm === 'PM' && hour !== 12) hour += 12;
    if (ampm === 'AM' && hour === 12) hour = 0;
    return hour < 10 ? '0' + hour : '' + hour;
  }

  @HostListener('document:click', ['$event'])
  clickout(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isDropdownOpen = false;
    }
  }
}